import { api } from "../../api";

export const tasksApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ==========================================
    // Task CRUD Operations
    // ==========================================

    // Get all tasks with optional filters
    getTasks: builder.query({
      query: (filters = {}) => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value);
          }
        });
        return `tasks?${params}`;
      },
      transformResponse: (response) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: "Tasks", id: _id })),
              { type: "Tasks", id: "LIST" },
            ]
          : [{ type: "Tasks", id: "LIST" }],
    }),

    // Get a single task by ID
    getTask: builder.query({
      query: (id) => `tasks/${id}`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, id) => [{ type: "Tasks", id }],
    }),

    // Create a new task
    createTask: builder.mutation({
      query: (taskData) => ({
        url: "tasks",
        method: "POST",
        body: taskData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { job, leg }) =>
        [
          { type: "Tasks", id: "LIST" },
          job && { type: "JobTasks", id: job },
          leg && { type: "LegTasks", id: leg },
        ].filter(Boolean),
    }),

    // Update a task
    updateTask: builder.mutation({
      query: ({ taskId, ...updateData }) => ({
        url: `tasks/${taskId}`,
        method: "PUT",
        body: updateData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { taskId }) =>
        [
          { type: "Tasks", id: taskId },
          { type: "Tasks", id: "LIST" },
          result?.job && { type: "JobTasks", id: result.job },
          result?.leg && { type: "LegTasks", id: result.leg },
        ].filter(Boolean),
    }),

    // Delete a task
    deleteTask: builder.mutation({
      query: (taskId) => ({
        url: `tasks/${taskId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Tasks", id: "LIST" }],
    }),

    // Bulk update tasks (e.g., reorder)
    bulkUpdateTasks: builder.mutation({
      query: (updates) => ({
        url: "tasks/bulk/update",
        method: "PUT",
        body: { updates },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: [{ type: "Tasks", id: "LIST" }],
    }),

    // ==========================================
    // Job-specific Task Operations
    // ==========================================

    // Get tasks for a specific job
    getJobTasks: builder.query({
      query: (jobId) => `tasks/job/${jobId}`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, jobId) => [
        { type: "JobTasks", id: jobId },
        ...(result?.tasks || []).map(({ _id }) => ({ type: "Tasks", id: _id })),
      ],
    }),

    // Apply templates to a job
    applyTemplatesToJob: builder.mutation({
      query: ({ jobId, appType }) => ({
        url: `tasks/job/${jobId}/apply-templates`,
        method: "POST",
        body: { appType },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { jobId }) => [
        { type: "JobTasks", id: jobId },
        { type: "Jobs", id: jobId },
      ],
    }),

    // Apply a template group to a job
    applyTemplateGroupToJob: builder.mutation({
      query: ({ jobId, groupKey, appType, mode = "dedupe" }) => ({
        url: `tasks/job/${jobId}/apply-group`,
        method: "POST",
        body: { groupKey, appType, mode },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { jobId }) => [
        { type: "JobTasks", id: jobId },
        { type: "Jobs", id: jobId },
      ],
    }),

    // ==========================================
    // Leg-specific Task Operations
    // ==========================================

    // Get tasks for a specific leg
    getLegTasks: builder.query({
      query: (legId) => `legs/${legId}/tasks`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, legId) => [
        { type: "LegTasks", id: legId },
        ...(result?.tasks || []).map(({ _id }) => ({ type: "Tasks", id: _id })),
      ],
    }),

    // Create task for a leg
    createLegTask: builder.mutation({
      query: ({ legId, ...taskData }) => ({
        url: `legs/${legId}/tasks`,
        method: "POST",
        body: taskData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { legId }) => [
        { type: "LegTasks", id: legId },
        { type: "Legs", id: legId },
      ],
    }),

    // Update task for a leg
    updateLegTask: builder.mutation({
      query: ({ legId, taskId, ...updateData }) => ({
        url: `legs/${legId}/tasks/${taskId}`,
        method: "PUT",
        body: updateData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { legId, taskId }) => [
        { type: "LegTasks", id: legId },
        { type: "Tasks", id: taskId },
      ],
    }),

    // Delete task from a leg
    deleteLegTask: builder.mutation({
      query: ({ legId, taskId }) => ({
        url: `legs/${legId}/tasks/${taskId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { legId }) => [
        { type: "LegTasks", id: legId },
        { type: "Legs", id: legId },
      ],
    }),

    // Apply templates to a leg
    applyTemplatesToLeg: builder.mutation({
      query: ({ legId, appType }) => ({
        url: `tasks/leg/${legId}/apply-templates`,
        method: "POST",
        body: { appType },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { legId }) => [
        { type: "LegTasks", id: legId },
        { type: "Legs", id: legId },
      ],
    }),

    // Apply a template group to a leg
    applyTemplateGroupToLeg: builder.mutation({
      query: ({ legId, groupKey, appType, mode = "dedupe" }) => ({
        url: `tasks/leg/${legId}/apply-group`,
        method: "POST",
        body: { groupKey, appType, mode },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { legId }) => [
        { type: "LegTasks", id: legId },
        { type: "Legs", id: legId },
      ],
    }),

    // ==========================================
    // Task Template Operations
    // ==========================================

    // Get all task templates
    getTaskTemplates: builder.query({
      query: (filters = {}) => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value);
          }
        });
        return `tasks/templates?${params}`;
      },
      transformResponse: (response) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: "TaskTemplates", id: _id })),
              { type: "TaskTemplates", id: "LIST" },
            ]
          : [{ type: "TaskTemplates", id: "LIST" }],
    }),

    // Get a single task template
    getTaskTemplate: builder.query({
      query: (id) => `tasks/templates/${id}`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, id) => [{ type: "TaskTemplates", id }],
    }),

    // Create a task template
    createTaskTemplate: builder.mutation({
      query: (templateData) => ({
        url: "tasks/templates",
        method: "POST",
        body: templateData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: [{ type: "TaskTemplates", id: "LIST" }],
    }),

    // Update a task template
    updateTaskTemplate: builder.mutation({
      query: ({ templateId, id, ...updateData }) => ({
        url: `tasks/templates/${templateId || id}`,
        method: "PUT",
        body: updateData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { templateId, id }) => [
        { type: "TaskTemplates", id: templateId || id },
        { type: "TaskTemplates", id: "LIST" },
      ],
    }),

    // Delete a task template
    deleteTaskTemplate: builder.mutation({
      query: (arg) => {
        const templateId = typeof arg === "string" ? arg : arg?.templateId || arg?.id;
        return {
        url: `tasks/templates/${templateId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: [{ type: "TaskTemplates", id: "LIST" }],
    }),

    // ==========================================
    // Task Template Group Operations
    // ==========================================

    getTaskTemplateGroups: builder.query({
      query: (filters = {}) => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value);
          }
        });
        return `tasks/template-groups?${params}`;
      },
      transformResponse: (response) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: "TaskTemplateGroups", id: _id })),
              { type: "TaskTemplateGroups", id: "LIST" },
            ]
          : [{ type: "TaskTemplateGroups", id: "LIST" }],
    }),

    createTaskTemplateGroup: builder.mutation({
      query: (groupData) => ({
        url: "tasks/template-groups",
        method: "POST",
        body: groupData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: [{ type: "TaskTemplateGroups", id: "LIST" }],
    }),

    updateTaskTemplateGroup: builder.mutation({
      query: ({ groupId, ...updateData }) => ({
        url: `tasks/template-groups/${groupId}`,
        method: "PUT",
        body: updateData,
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: (result, error, { groupId }) => [
        { type: "TaskTemplateGroups", id: groupId },
        { type: "TaskTemplateGroups", id: "LIST" },
      ],
    }),

    deleteTaskTemplateGroup: builder.mutation({
      query: (groupId) => ({
        url: `tasks/template-groups/${groupId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "TaskTemplateGroups", id: "LIST" }],
    }),
  }),
});

export const {
  // Task CRUD
  useGetTasksQuery,
  useGetTaskQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useBulkUpdateTasksMutation,

  // Job Tasks
  useGetJobTasksQuery,
  useApplyTemplatesToJobMutation,
  useApplyTemplateGroupToJobMutation,

  // Leg Tasks
  useGetLegTasksQuery,
  useCreateLegTaskMutation,
  useUpdateLegTaskMutation,
  useDeleteLegTaskMutation,
  useApplyTemplatesToLegMutation,
  useApplyTemplateGroupToLegMutation,

  // Task Templates
  useGetTaskTemplatesQuery,
  useGetTaskTemplateQuery,
  useCreateTaskTemplateMutation,
  useUpdateTaskTemplateMutation,
  useDeleteTaskTemplateMutation,

  // Task Template Groups
  useGetTaskTemplateGroupsQuery,
  useCreateTaskTemplateGroupMutation,
  useUpdateTaskTemplateGroupMutation,
  useDeleteTaskTemplateGroupMutation,
} = tasksApi;

